import jwtAxios from '@crema/services/auth/jwt-auth';
import {API_CONFIG, buildMediaUrl} from '../config/api.config';

const API_BASE = API_CONFIG.ADMIN_PREFIX;

/**
 * Normalize API response
 */
const normalizeResponse = (data) => {
  if (!data) return null;
  if (data.success !== undefined) {
    return data.data || data;
  }
  return data;
};

/**
 * Normalize list response with pagination
 */
const normalizeList = (data) => {
  if (!data) return {items: [], pagination: {}};

  const normalized = normalizeResponse(data);

  if (Array.isArray(normalized)) {
    return {
      items: normalized,
      pagination: {},
    };
  }

  if (normalized && normalized.data) {
    return {
      items: normalized.data,
      pagination: {
        current_page: normalized.current_page || 1,
        per_page: normalized.per_page || 15,
        total: normalized.total || 0,
        total_pages: normalized.last_page || 1,
      },
    };
  }

  return {
    items: normalized ? [normalized] : [],
    pagination: {},
  };
};

/**
 * Media API Service
 */
class MediaApi {
  // ==================== Photo Albums ====================

  async getPhotoAlbums(params = {}) {
    try {
      const response = await jwtAxios.get(`${API_BASE}/photo-albums`, {params});
      return normalizeList(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async getPhotoAlbum(id) {
    try {
      const response = await jwtAxios.get(`${API_BASE}/photo-albums/${id}`);
      return normalizeResponse(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async createPhotoAlbum(albumData) {
    try {
      const response = await jwtAxios.post(
        `${API_BASE}/photo-albums`,
        albumData,
      );
      return normalizeResponse(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async updatePhotoAlbum(id, albumData) {
    try {
      const response = await jwtAxios.put(
        `${API_BASE}/photo-albums/${id}`,
        albumData,
      );
      return normalizeResponse(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async deletePhotoAlbum(id) {
    try {
      const response = await jwtAxios.delete(`${API_BASE}/photo-albums/${id}`);
      return normalizeResponse(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==================== Video Albums ====================

  async getVideoAlbums(params = {}) {
    try {
      const response = await jwtAxios.get(`${API_BASE}/video-albums`, {params});
      return normalizeList(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async getVideoAlbum(id) {
    try {
      const response = await jwtAxios.get(`${API_BASE}/video-albums/${id}`);
      return normalizeResponse(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async createVideoAlbum(albumData) {
    try {
      const response = await jwtAxios.post(
        `${API_BASE}/video-albums`,
        albumData,
      );
      return normalizeResponse(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async updateVideoAlbum(id, albumData) {
    try {
      const response = await jwtAxios.put(
        `${API_BASE}/video-albums/${id}`,
        albumData,
      );
      return normalizeResponse(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async deleteVideoAlbum(id) {
    try {
      const response = await jwtAxios.delete(`${API_BASE}/video-albums/${id}`);
      return normalizeResponse(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==================== Videos ====================

  async getVideos(params = {}) {
    try {
      const response = await jwtAxios.get(`${API_BASE}/videos`, {params});
      return normalizeList(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async getVideo(id) {
    try {
      const response = await jwtAxios.get(`${API_BASE}/videos/${id}`);
      return normalizeResponse(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async createVideo(videoData) {
    try {
      const response = await jwtAxios.post(`${API_BASE}/videos`, videoData);
      return normalizeResponse(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async updateVideo(id, videoData) {
    try {
      const response = await jwtAxios.post(`${API_BASE}/videos/${id}`, {
        ...videoData,
        _method: 'PUT',
      });
      return normalizeResponse(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async deleteVideo(id) {
    try {
      const response = await jwtAxios.delete(`${API_BASE}/videos/${id}`);
      return normalizeResponse(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==================== Media Library ====================

  async getMediaFiles(params = {}) {
    try {
      const response = await jwtAxios.get(`${API_BASE}/media`, {params});
      const normalized = normalizeResponse(response.data);

      // Media API returns {files: [], pagination: {}}
      if (normalized && normalized.files) {
        return {
          files: normalized.files || [],
          pagination: normalized.pagination || {
            current_page: 1,
            per_page: 24,
            total: 0,
            total_pages: 1,
          },
        };
      }

      // Fallback to normalizeList for other formats
      return normalizeList(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async uploadMediaFile(
    file,
    collection = 'media',
    name = null,
    folderId = null,
  ) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('collection', collection);
      if (name) {
        formData.append('name', name);
      }
      if (folderId) {
        formData.append('folder_id', folderId);
      }

      // Debug: Log FormData contents
      console.log('Uploading file:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        collection,
        folderId,
        formDataKeys: Array.from(formData.keys()),
      });

      // Don't set Content-Type header - let browser/axios set it automatically with boundary
      // The interceptor will remove Content-Type for FormData
      const response = await jwtAxios.post(
        `${API_BASE}/media/upload`,
        formData,
        {
          // Explicitly set headers to ensure FormData is sent correctly
          headers: {
            // Don't set Content-Type - let browser set it with boundary
          },
          // Ensure FormData is not transformed
          transformRequest: [
            (data) => {
              // Return FormData as-is
              return data;
            },
          ],
        },
      );
      return normalizeResponse(response.data);
    } catch (error) {
      console.error('Upload error:', error);
      throw error.response?.data || error;
    }
  }

  async renameMediaFile(fileId, newName) {
    try {
      const response = await jwtAxios.put(`${API_BASE}/media/rename`, {
        id: fileId,
        name: newName,
      });
      const normalized = normalizeResponse(response.data);
      // If normalized is { file: {...} }, extract the file object
      if (normalized?.file && typeof normalized.file === 'object') {
        return normalized.file;
      }
      return normalized;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async deleteMediaFile(fileId) {
    try {
      const response = await jwtAxios.delete(`${API_BASE}/media/delete`, {
        data: {
          id: fileId,
        },
      });
      return normalizeResponse(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async getFileInfo(paths) {
    try {
      const pathsArray = Array.isArray(paths) ? paths : [paths];
      const response = await jwtAxios.post(`${API_BASE}/media/file-info`, {
        paths: pathsArray,
      });
      return normalizeResponse(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  getMediaFileUrl(path) {
    return buildMediaUrl(path);
  }

  // ==================== Media Folders ====================

  async getFolders(params = {}) {
    try {
      const response = await jwtAxios.get(`${API_BASE}/media-folders`, {
        params,
      });
      return normalizeResponse(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async getFolder(id) {
    try {
      const response = await jwtAxios.get(`${API_BASE}/media-folders/${id}`);
      return normalizeResponse(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async createFolder(folderData) {
    try {
      const response = await jwtAxios.post(
        `${API_BASE}/media-folders`,
        folderData,
      );
      return normalizeResponse(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async updateFolder(id, folderData) {
    try {
      const response = await jwtAxios.put(
        `${API_BASE}/media-folders/${id}`,
        folderData,
      );
      return normalizeResponse(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  async deleteFolder(id) {
    try {
      const response = await jwtAxios.delete(`${API_BASE}/media-folders/${id}`);
      return normalizeResponse(response.data);
    } catch (error) {
      throw error.response?.data || error;
    }
  }
}

export default new MediaApi();
